import { type FC, useState } from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  Divider,
} from "@mui/material";
import { ViewIcon, ViewOffIcon } from "hugeicons-react";
import { toaster } from "@/components/ui/toaster";
import { useLogin } from "../graphql/__generated__/Login.generated";
import { useMeLazyQuery } from "../graphql/__generated__/GetMe.generated";

import "react-responsive-carousel/lib/styles/carousel.min.css";
import { Carousel } from "react-responsive-carousel";
import { Link } from "react-router";
import Logo from "../assets/logo.svg";
import img1 from "../assets/img1.jpg";
import img2 from "../assets/img2.jpg";
import img3 from "../assets/img3.jpg";
import img4 from "../assets/img4.jpg";

const LoginPage: FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [login] = useLogin();
  const [getUser] = useMeLazyQuery({
    onCompleted: (data) => {
      sessionStorage.setItem("data", JSON.stringify(data));
      window.location.href = data?.me?.usertype === "ADMIN" ? "/app/0" : "/app/partner/dashboard";
      setLoading(false);
    },
    onError: (error) => {
      const promise = new Promise<void>((resolve) => {
        setTimeout(() => resolve(), 1000);
      });
      toaster.promise(promise, {
        error: {
          title: "Login failed",
          description: error.message,
        },
        loading: { title: "Loading...", description: "Please wait" },
      });
    },
  });

  const handleLogin = () => {
    setLoading(true);
    toaster.create({
      title: "Loading...",
      description: "Please wait",
    });

    login({
      variables: {
        username,
        password,
        usertype: "USER",
      },
    })
      .then((res) => {
        if (res.data) {
          sessionStorage.setItem("token", res.data.Login.token);
          sessionStorage.setItem("refreshToken", res?.data?.Login?.refreshToken);

          const promise = new Promise<void>((resolve) => {
            setTimeout(() => resolve(), 1000);
          });
          toaster.dismiss();

          toaster.promise(promise, {
            loading: {
              title: "Logging in...",
              description: "Please wait",
            },
            success: {
              title: "Successfully Logged In!",
              description: "Looks great",
            },
          });
          getUser();
        }
      })
      .catch((error) => {
        setError(error.message);
        setLoading(false);
      });
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "common.white" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          width: { xs: "100%", md: "50%" },
          alignItems: "center",
          p: 4,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 480 }}>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              mb: 6,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                p: 1,
                bgcolor: "primary.50",
                borderRadius: 2,
              }}
            >
              <img src={Logo} height={40} width={40} alt="SheetX Logo" />
            </Box>
            <Typography sx={{ color: "text.primary", fontWeight: 700, fontSize: "42px" }}>
              SheetX
            </Typography>
          </Box>

          <Box sx={{ bgcolor: "common.white", borderRadius: 4, p: 4 }}>
            <Box sx={{ mb: 3, textAlign: "center" }}>
              <Typography
                sx={{ fontSize: "1.875rem", fontWeight: 700, color: "text.primary", mb: 1 }}
              >
                Welcome Back
              </Typography>
              <Typography sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
                Access your dashboard and manage your warehouse operations
              </Typography>
            </Box>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (document.activeElement && document.activeElement.tagName === "TEXTAREA") {
                  return;
                }
                handleLogin();
              }}
            >
              <Stack spacing={3}>
                <TextField
                  required
                  label="Username or Email"
                  placeholder="Enter your username or email"
                  onChange={(e) => setUsername(e.target.value)}
                  value={username}
                  fullWidth
                />

                <TextField
                  required
                  label="Password"
                  placeholder="Enter your password"
                  type={showPassword ? "text" : "password"}
                  onChange={(e) => setPassword(e.target.value)}
                  value={password}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((prev) => !prev)}
                          edge="end"
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          {showPassword ? <ViewIcon /> : <ViewOffIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {error && <Alert severity="error">{error}</Alert>}

                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Link
                    to="/forgot-password"
                    style={{
                      color: "#1976d2",
                      fontSize: "14px",
                      fontWeight: "500",
                    }}
                  >
                    Forgot password?
                  </Link>
                </Box>
              </Stack>

              <Box sx={{ mt: 3 }}>
                <Button
                  type="submit"
                  fullWidth
                  size="large"
                  variant="contained"
                  disabled={loading}
                  sx={{
                    background: "#1976d2",
                    color: "#fff",
                    borderRadius: 3,
                    fontWeight: 600,
                    textTransform: "none",
                    transition: "all 0.2s",
                    "&:hover": {
                      background: "#1565c0",
                      transform: "translateY(-2px)",
                    },
                    "&:active": {
                      transform: "translateY(0)",
                    },
                  }}
                >
                  Sign In
                </Button>
              </Box>

              <Box sx={{ mt: 2, textAlign: "center" }}>
                <Typography sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
                  By signing in, you agree to our{" "}
                  <Link
                    to="/privacy-policy"
                    style={{
                      color: "#1976d2",
                      fontWeight: "500",
                      textDecoration: "underline",
                    }}
                  >
                    Privacy Policy
                  </Link>
                </Typography>
              </Box>
            </form>

            <Divider sx={{ my: 3 }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                OR
              </Typography>
            </Divider>

            <Box sx={{ textAlign: "center" }}>
              <Typography sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
                Are you an administrator?{" "}
                <Link
                  to="/admin"
                  style={{
                    color: "#1976d2",
                    fontWeight: "600",
                    textDecoration: "none",
                  }}
                >
                  Log in here
                </Link>
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{ width: "50%", display: { xs: "none", md: "block" }, height: "100vh" }}>
        <Carousel
          autoPlay
          infiniteLoop
          showThumbs={false}
          showArrows={false}
          showStatus={false}
          interval={5000}
          transitionTime={1000}
        >
          <Box sx={{ height: "100vh", overflow: "hidden" }}>
            <img
              src={img1}
              alt="Cold storage facility"
              style={{
                height: "100vh",
                width: "100%",
                objectFit: "cover",
              }}
            />
          </Box>
          <Box sx={{ height: "100vh", overflow: "hidden" }}>
            <img
              src={img2}
              alt="Warehouse management"
              style={{
                height: "100vh",
                width: "100%",
                objectFit: "cover",
              }}
            />
          </Box>
          <Box sx={{ height: "100vh", overflow: "hidden" }}>
            <img
              src={img3}
              alt="Inventory tracking"
              style={{
                height: "100vh",
                width: "100%",
                objectFit: "cover",
              }}
            />
          </Box>
          <Box sx={{ height: "100vh", overflow: "hidden" }}>
            <img
              src={img4}
              alt="Supply chain network"
              style={{
                height: "100vh",
                width: "100%",
                objectFit: "cover",
              }}
            />
          </Box>
        </Carousel>
      </Box>
    </Box>
  );
};

export default LoginPage;
